import { inject, Injectable } from '@angular/core';
import { first } from 'lodash-es';
import { combineLatest, Subscriber } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  TrialInsightsPaymentMilestoneResponseType,
  TrialInsightsResponseGeneric,
  TrialInsightsState,
} from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsStore } from '@models/trial-insights/trial-insights.store';

import { GqlService } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';
import { MainQuery } from '@shared/store/main/main.query';

type LoadingState = 'completed' | RequestType;
type RequestType = 'initial' | 'remaining';
type Response = [
  TrialInsightsResponseGeneric<ResponseType[]>,
  TrialInsightsResponseGeneric<ResponseType[]>,
  TrialInsightsResponseGeneric<ResponseType[]>,
];
type ResponseType = TrialInsightsPaymentMilestoneResponseType;
type State = Partial<TrialInsightsState['paymentMilestone']>;

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsPaymentMilestoneStoreService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private store = inject(TrialInsightsStore);

  getPaymentMilestones$() {
    return this.mainQuery.select('trialKey').pipe(
      this.updateLoadingState('initial'),
      switchMap(() =>
        combineLatest([
          this.gqlService.listPaymentMilestoneSummary$([]), // total
          // fetch table data
          this.gqlService.listPaymentMilestoneSummary$([
            'PERIOD_MONTH',
            'ORGANIZATION',
            'MILESTONE',
          ]),
          // fetch chart data
          this.gqlService.listPaymentMilestoneSummary$(['PERIOD_QUARTER']),
        ])
      ),
      this.processResponse(),
      this.updateLoadingState('completed')
    );
  }

  loadingState(type: LoadingState): State {
    const loadingState: State = {};

    if (type === 'initial') {
      loadingState.isLoading = true;
      loadingState.data = null;
    } else {
      loadingState.isLoading = false;
      loadingState.isLoadingRemaining = false;
    }

    return loadingState;
  }

  processResponse() {
    const processFn = (response: Response, subscriber: Subscriber<unknown>) => {
      const [{ data: totalData }, { data: tableData }, { data: chartData }] = response;

      this.store.update((state) => {
        return {
          ...state,
          paymentMilestone: {
            ...state.paymentMilestone,
            chartData: chartData ? chartData : [],
            data: tableData ? tableData : [],
            totalMilestones: first(totalData)?.total_amount || 0,
          },
        };
      });

      subscriber.next(tableData);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }

  updateLoadingState(type: LoadingState) {
    const updates = this.loadingState(type);

    const processFn = (_: unknown, subscriber: Subscriber<unknown>) => {
      this.store.update((state) => {
        return {
          ...state,
          paymentMilestone: {
            ...state.paymentMilestone,
            ...updates,
            data: [...(state.paymentMilestone.data || []), ...(updates.data || [])],
          },
        };
      });

      subscriber.next(updates);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }
}
